import { useEffect, useState } from "react";
import { Welcome } from "@/steps/Welcome";
import { Disclaimer } from "@/steps/Disclaimer";
import { MenstrualStep } from "@/steps/MenstrualStep";
import { SymptomsStep } from "@/steps/SymptomsStep";
import { ImpactStep } from "@/steps/ImpactStep";
import { EmotionalStep } from "@/steps/EmotionalStep";
import { ImmediateAlertsStep } from "@/steps/ImmediateAlertsStep";
import { PriorityAlertsStep } from "@/steps/PriorityAlertsStep";
import { LifestyleStep } from "@/steps/LifestyleStep";
import { Review } from "@/steps/Review";
import { Result } from "@/steps/Result";
import { UrgentHalt } from "@/steps/UrgentHalt";
import { SelfHarmHalt } from "@/steps/SelfHarmHalt";
import { PrivacyFooter } from "@/components/PrivacyFooter";

const STEPS = [
  "welcome",
  "disclaimer",
  "menstrual",
  "symptoms",
  "impact",
  "emotional",
  "immediate",
  "priority",
  "lifestyle",
  "review",
  "result",
];

const emptyState = () => ({
  consent: false,
  menstrual: {},
  symptoms: {},
  impact: {},
  emotional: {},
  immediate: {},
  priority: {},
  lifestyle: { stress: 5 },
  halt: null, // 'urgent' | 'selfharm' | null
});

export default function Climaterio() {
  const [state, setState] = useState(emptyState);
  const [stepIdx, setStepIdx] = useState(0);

  // Sin persistencia: al recargar todo vuelve a empty.
  useEffect(() => {
    try {
      window.sessionStorage.clear();
      window.localStorage.removeItem("climaterio-claro");
    } catch {
      /* noop */
    }
  }, []);

  const goto = (name) => setStepIdx(STEPS.indexOf(name));
  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const setSection = (section, id, value) =>
    setState((s) => ({ ...s, [section]: { ...s[section], [id]: value } }));

  const setConsent = (v) => setState((s) => ({ ...s, consent: v }));

  const reset = () => {
    setState(emptyState());
    setStepIdx(0);
  };

  if (state.halt === "urgent") {
    return (
      <>
        <UrgentHalt onRestart={reset} />
        <PrivacyFooter />
      </>
    );
  }
  if (state.halt === "selfharm") {
    return (
      <>
        <SelfHarmHalt onRestart={reset} />
        <PrivacyFooter />
      </>
    );
  }

  const step = STEPS[stepIdx];
  const progress = Math.round(((stepIdx + 1) / STEPS.length) * 100);

  let content;
  switch (step) {
    case "welcome":
      content = <Welcome onStart={next} />;
      break;
    case "disclaimer":
      content = (
        <Disclaimer
          consent={state.consent}
          setConsent={setConsent}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "menstrual":
      content = (
        <MenstrualStep
          progress={progress}
          answers={state.menstrual}
          setAnswer={(id, v) => setSection("menstrual", id, v)}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "symptoms":
      content = (
        <SymptomsStep
          progress={progress}
          answers={state.symptoms}
          setAnswer={(id, v) => setSection("symptoms", id, v)}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "impact":
      content = (
        <ImpactStep
          progress={progress}
          answers={state.impact}
          setAnswer={(id, v) => setSection("impact", id, v)}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "emotional":
      content = (
        <EmotionalStep
          progress={progress}
          answers={state.emotional}
          setAnswer={(id, v) => setSection("emotional", id, v)}
          onBack={back}
          onNext={next}
          onSelfHarm={() => setState((s) => ({ ...s, halt: "selfharm" }))}
        />
      );
      break;
    case "immediate":
      content = (
        <ImmediateAlertsStep
          progress={progress}
          answers={state.immediate}
          setAnswer={(id, v) => setSection("immediate", id, v)}
          onBack={back}
          onNext={next}
          onUrgent={() => setState((s) => ({ ...s, halt: "urgent" }))}
        />
      );
      break;
    case "priority":
      content = (
        <PriorityAlertsStep
          progress={progress}
          answers={state.priority}
          setAnswer={(id, v) => setSection("priority", id, v)}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "lifestyle":
      content = (
        <LifestyleStep
          progress={progress}
          answers={state.lifestyle}
          setAnswer={(id, v) => setSection("lifestyle", id, v)}
          onBack={back}
          onNext={next}
        />
      );
      break;
    case "review":
      content = (
        <Review
          progress={progress}
          state={state}
          onBack={back}
          onSubmit={() => goto("result")}
        />
      );
      break;
    case "result":
      content = <Result state={state} onRestart={reset} onErase={reset} />;
      break;
    default:
      content = <Welcome onStart={next} />;
  }

  return (
    <>
      {content}
      <PrivacyFooter />
    </>
  );
}
